import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { useQueryClient } from '@tanstack/react-query';

import { useAgent } from '@codes/agents';
import { chatKeys, generateUUID, useChatMessages, useSendMessageV2 } from '@codes/chatbots';
import { useWebCoreStore } from '@codes/web-core';

import type { ChatView } from '@lemoncloud/ssocio-chatbots-api';

interface UseMyAgentChatProps {
    agentId: string;
    chatId: string;
}

interface ChatViewWithError extends ChatView {
    isError?: boolean;
}

export const useMyAgentChat = ({ agentId, chatId }: UseMyAgentChatProps) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { profile } = useWebCoreStore();

    // State management
    const [messages, setMessages] = useState<ChatView[]>([]);
    const [pendingMessage, setPendingMessage] = useState<ChatViewWithError | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [isWaitingResponse, setIsWaitingResponse] = useState(false);
    const [input, setInput] = useState('');
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const initializedRef = useRef(false);

    // Data fetching
    const { data: agentData, isLoading: isAgentLoading } = useAgent(agentId, !!agentId);
    const {
        data: messagesData,
        isLoading: isMessagesLoading,
        fetchNextPage,
        hasNextPage,
    } = useChatMessages({
        rootId: chatId,
        limit: 20,
    });

    const sendMessage = useSendMessageV2();

    // Load messages from current chat
    useEffect(() => {
        if (messagesData?.pages) {
            const allMessages = messagesData.pages.reduce<ChatView[]>((acc, page) => {
                return [...acc, ...(page.data || [])];
            }, []);

            // Process documentIds from search messages before filtering
            const processedMessages = allMessages.map((msg, index) => {
                if (msg.stereo === 'answer' && !msg.documentIds) {
                    // Look for the most recent search message before this answer
                    for (let i = index - 1; i >= 0; i--) {
                        const prevMessage = allMessages[i];
                        if (prevMessage.stereo === 'search' && prevMessage.documentIds) {
                            return { ...msg, documentIds: prevMessage.documentIds };
                        }
                    }
                }
                return msg;
            });

            const filteredMessages = processedMessages.filter(msg => msg.id && msg.content && !msg.hidden);
            const sortedMessages = filteredMessages.sort((chatA, chatB) => {
                const aOrderNo = chatA.childNo ?? chatA.no ?? 0;
                const bOrderNo = chatB.childNo ?? chatB.no ?? 0;
                return aOrderNo - bOrderNo;
            });

            setMessages(sortedMessages);
            setHasMore(hasNextPage || false);
        }
    }, [messagesData, hasNextPage]);

    // Initialize chat
    useEffect(() => {
        if (!initializedRef.current && profile && agentId && chatId && !isAgentLoading) {
            initializedRef.current = true;
            setIsLoading(true);
            // Initial data is loaded by the queries above
            setIsLoading(false);
        }
    }, [profile, agentId, chatId, isAgentLoading]);

    // Load more messages
    const loadMoreMessages = useCallback(async () => {
        if (isLoadingMore || !hasMore) {
            return;
        }

        try {
            setIsLoadingMore(true);
            await fetchNextPage();
        } catch (error) {
            console.error('Failed to load more messages:', error);
            toast({ title: t('chat.load_more_failed') });
        } finally {
            setIsLoadingMore(false);
        }
    }, [fetchNextPage, isLoadingMore, hasMore, t]);

    // Message handling
    const clearPendingMessage = useCallback(() => {
        setPendingMessage(null);
        setIsWaitingResponse(false);
    }, []);

    const addMessage = useCallback(
        async (messageContent: string) => {
            if (!messageContent.trim() || !chatId || isWaitingResponse) {
                return;
            }

            const userMessage: ChatViewWithError = {
                id: generateUUID(),
                content: messageContent.trim(),
                stereo: 'query',
                createdAt: Date.now(),
                childNo: (messages.length + 1) * 10,
            };

            setPendingMessage(userMessage);
            setIsWaitingResponse(true);
            setInput('');

            try {
                const res = await sendMessage.mutateAsync({
                    groupId: chatId,
                    params: { sid: profile.sid },
                    body: {
                        ...(profile?.sid && { sid: profile.sid }),
                        content: messageContent.trim(),
                    },
                });

                await updateChatMessages(chatId, res.list || []);
                clearPendingMessage();
            } catch (error) {
                console.error('Failed to send message:', error);
                setPendingMessage(prev => (prev ? { ...prev, isError: true } : null));
                setIsWaitingResponse(false);
                toast({ title: t('chat.send_message_failed') });
            }
        },
        [chatId, messages.length, sendMessage, profile, clearPendingMessage, t, isWaitingResponse]
    );

    // Update chat messages in cache
    const updateChatMessages = async (chatId: string, newMessages: ChatView[]) => {
        await queryClient.setQueryData(chatKeys.list({ rootId: chatId, limit: 20 }), (oldData: any) => {
            if (!oldData) {
                return oldData;
            }

            const newFirstPage = {
                ...oldData.pages[0],
                data: [...(oldData.pages[0].data || []), ...newMessages],
            };
            return {
                ...oldData,
                pages: [newFirstPage, ...oldData.pages.slice(1)],
                pageParams: oldData.pageParams,
            };
        });
    };

    // Computed values
    const displayMessages = useMemo(() => {
        const allMessages = [...messages];
        if (pendingMessage) {
            allMessages.push(pendingMessage);
        }
        return allMessages
            .filter(chat => !chat.hidden && (chat.stereo === 'query' || chat.stereo === 'answer'))
            .sort((chatA, chatB) => {
                const aOrderNo = chatA.childNo ?? chatA.no ?? 0;
                const bOrderNo = chatB.childNo ?? chatB.no ?? 0;
                return aOrderNo - bOrderNo;
            });
    }, [messages, pendingMessage]);

    return {
        agentData,
        displayMessages,
        input,
        setInput,
        addMessage,
        isLoading: isLoading || isAgentLoading,
        isMessagesLoading,
        isWaitingResponse,
        isLoadingMore,
        hasMore,
        loadMoreMessages,
    };
};
